import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Tabs,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../utils/consts";
import "./DocumentsSettings.scss";

// ── Types ──────────────────────────────────────────────────────────────────────
export type DocRole = {
  _id: string;
  name: string;
  permissions: DocPermission[];
};

export type DocGroup = {
  _id: string;
  name: string;
  description?: string;
  memberIds: string[];
};

export type DocPermission = "view" | "upload" | "edit" | "delete";

const ALL_PERMISSIONS: { key: DocPermission; label: string }[] = [
  { key: "view", label: "View" },
  { key: "upload", label: "Upload" },
  { key: "edit", label: "Edit / Rename" },
  { key: "delete", label: "Delete" },
];

const PERM_COLOR: Record<DocPermission, string> = {
  view: "blue",
  upload: "green",
  edit: "orange",
  delete: "red",
};

// ── Component ──────────────────────────────────────────────────────────────────
const DocumentsSettings: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const clientId: string =
    typeof userData?.clientId === "object"
      ? userData.clientId._id
      : userData?.clientId;

  const [users, setUsers] = useState<any[]>([]);

  const fetchUsers = useCallback(() => {
    if (!clientId) return;
    axios
      .get(`${BASE_API_URL}/${clientId}/users`)
      .then((res) => setUsers(res.data))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const tabItems = [
    {
      key: "groups",
      label: t("Groups"),
      children: <GroupsTab clientId={clientId} users={users} t={t} />,
    },
    {
      key: "roles",
      label: t("Roles"),
      children: <RolesTab clientId={clientId} t={t} />,
    },
  ];

  return (
    <div className="ds-wrap">
      <div className="ds-header">
        <div className="ds-header-left">
          <h2 className="ds-title">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <line x1="9" y1="14" x2="15" y2="14" />
            </svg>
            {t("Documents Access")}
          </h2>
          <p className="ds-subtitle">
            {t(
              "Manage user groups and roles to control who can access document folders."
            )}
          </p>
        </div>
      </div>
      <Tabs items={tabItems} className="ds-tabs" />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// GROUPS TAB
// ════════════════════════════════════════════════════════════════════════════════
const GroupsTab: React.FC<{ clientId: string; users: any[]; t: any }> = ({
  clientId,
  users,
  t,
}) => {
  const [groups, setGroups] = useState<DocGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DocGroup | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id: string;
    name: string;
  }>({ open: false, id: "", name: "" });
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();

  const fetchGroups = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/doc-groups`)
      .then((res) => setGroups(res.data))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openCreate = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (group: DocGroup) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      description: group.description,
      memberIds: group.memberIds,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingGroup) {
        await axios.put(
          `${BASE_API_URL}/${clientId}/doc-groups/${editingGroup._id}`,
          values
        );
        message.success(t("Group updated"));
      } else {
        await axios.post(`${BASE_API_URL}/${clientId}/doc-groups`, values);
        message.success(t("Group created"));
      }
      setModalOpen(false);
      fetchGroups();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(t("Error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${BASE_API_URL}/${clientId}/doc-groups/${deleteModal.id}`
      );
      message.success(t("Group deleted"));
      setDeleteModal({ open: false, id: "", name: "" });
      fetchGroups();
    } catch {
      message.error(t("Error"));
    } finally {
      setDeleting(false);
    }
  };

  const userOptions = users.map((u) => ({
    value: u._id,
    label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
  }));

  const columns: ColumnsType<DocGroup> = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      render: (val) => <span className="ds-group-name">{val}</span>,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      render: (val) => (
        <span className="ds-muted">{val || "—"}</span>
      ),
    },
    {
      title: t("Members"),
      dataIndex: "memberIds",
      key: "members",
      render: (ids: string[]) => {
        if (!ids?.length) return <span className="ds-muted">—</span>;
        const shown = ids.slice(0, 3);
        const rest = ids.length - shown.length;
        return (
          <div className="ds-member-tags">
            {shown.map((id) => {
              const u = users.find((x) => x._id === id);
              const label = u
                ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email
                : id;
              return (
                <Tag key={id} className="ds-member-tag">
                  {label}
                </Tag>
              );
            })}
            {rest > 0 && (
              <Tag className="ds-member-tag ds-member-tag--more">
                +{rest}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="ds-row-actions">
          <button
            className="ds-act-btn"
            title={t("Edit")}
            onClick={() => openEdit(record)}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 20h4L20 8l-4-4L4 16z" />
            </svg>
          </button>
          <button
            className="ds-act-btn ds-act-btn--danger"
            title={t("Delete")}
            onClick={() =>
              setDeleteModal({ open: true, id: record._id, name: record.name })
            }
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="ds-tab-content">
      <div className="ds-tab-bar">
        <p className="ds-tab-desc">
          {t(
            "Create groups of employees and assign them to folders instead of adding users one by one."
          )}
        </p>
        <Button type="primary" className="ds-create-btn" onClick={openCreate}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("New Group")}
        </Button>
      </div>

      <Table
        dataSource={groups}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={false}
        className="ds-table"
        locale={{ emptyText: t("No groups yet") }}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingGroup ? t("Edit Group") : t("New Group")}
        okText={editingGroup ? t("Save") : t("Create")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        className="standardHeader standardFooter"
      >
        <Form form={form} layout="vertical" className="ds-form">
          <Form.Item
            name="name"
            label={t("Group name")}
            rules={[{ required: true, message: t("Required") }]}
          >
            <Input placeholder={t("e.g. Doctors, Admins, HR team")} autoFocus />
          </Form.Item>
          <Form.Item name="description" label={t("Description")}>
            <Input placeholder={t("Optional description")} />
          </Form.Item>
          <Form.Item name="memberIds" label={t("Members")}>
            <Select
              mode="multiple"
              placeholder={t("Select employees")}
              options={userOptions}
              filterOption={(input, opt) =>
                (opt?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        title={t("Delete Group")}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        okButtonProps={{ danger: true }}
        confirmLoading={deleting}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ open: false, id: "", name: "" })}
        className="standardHeader standardFooter"
      >
        <p>
          {t("Delete group")} <strong>"{deleteModal.name}"</strong>?{" "}
          {t(
            "Folders using this group will lose access for its members."
          )}
        </p>
      </Modal>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// ROLES TAB
// ════════════════════════════════════════════════════════════════════════════════
const RolesTab: React.FC<{ clientId: string; t: any }> = ({ clientId, t }) => {
  const [roles, setRoles] = useState<DocRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<DocRole | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id: string;
    name: string;
  }>({ open: false, id: "", name: "" });
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();

  const fetchRoles = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/doc-roles`)
      .then((res) => setRoles(res.data))
      .catch(() => setRoles([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const openCreate = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ permissions: ["view"] });
    setModalOpen(true);
  };

  const openEdit = (role: DocRole) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      permissions: role.permissions,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingRole) {
        await axios.put(
          `${BASE_API_URL}/${clientId}/doc-roles/${editingRole._id}`,
          values
        );
        message.success(t("Role updated"));
      } else {
        await axios.post(`${BASE_API_URL}/${clientId}/doc-roles`, values);
        message.success(t("Role created"));
      }
      setModalOpen(false);
      fetchRoles();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(t("Error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${BASE_API_URL}/${clientId}/doc-roles/${deleteModal.id}`
      );
      message.success(t("Role deleted"));
      setDeleteModal({ open: false, id: "", name: "" });
      fetchRoles();
    } catch {
      message.error(t("Error"));
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<DocRole> = [
    {
      title: t("Role"),
      dataIndex: "name",
      key: "name",
      render: (val) => <span className="ds-role-name">{val}</span>,
    },
    {
      title: t("Permissions"),
      dataIndex: "permissions",
      key: "permissions",
      render: (perms: DocPermission[]) => (
        <div className="ds-perm-tags">
          {ALL_PERMISSIONS.map(({ key, label }) => (
            <Tag
              key={key}
              color={perms?.includes(key) ? PERM_COLOR[key] : undefined}
              className={`ds-perm-tag${perms?.includes(key) ? "" : " ds-perm-tag--off"}`}
            >
              {t(label)}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="ds-row-actions">
          <button
            className="ds-act-btn"
            title={t("Edit")}
            onClick={() => openEdit(record)}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 20h4L20 8l-4-4L4 16z" />
            </svg>
          </button>
          <button
            className="ds-act-btn ds-act-btn--danger"
            title={t("Delete")}
            onClick={() =>
              setDeleteModal({ open: true, id: record._id, name: record.name })
            }
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="ds-tab-content">
      <div className="ds-tab-bar">
        <p className="ds-tab-desc">
          {t(
            "Define what actions each role can perform on documents: view, upload, edit, or delete."
          )}
        </p>
        <Button type="primary" className="ds-create-btn" onClick={openCreate}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("New Role")}
        </Button>
      </div>

      <Table
        dataSource={roles}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={false}
        className="ds-table"
        locale={{ emptyText: t("No roles yet") }}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingRole ? t("Edit Role") : t("New Role")}
        okText={editingRole ? t("Save") : t("Create")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        className="standardHeader standardFooter"
      >
        <Form form={form} layout="vertical" className="ds-form">
          <Form.Item
            name="name"
            label={t("Role name")}
            rules={[{ required: true, message: t("Required") }]}
          >
            <Input
              placeholder={t("e.g. Viewer, Editor, Full Access")}
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="permissions"
            label={t("Permissions")}
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: t("Select at least one permission"),
              },
            ]}
          >
            <Checkbox.Group className="ds-perm-checkboxes">
              {ALL_PERMISSIONS.map(({ key, label }) => (
                <Checkbox key={key} value={key} className="ds-perm-checkbox">
                  <span className={`ds-perm-label ds-perm-label--${key}`}>
                    {t(label)}
                  </span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        title={t("Delete Role")}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        okButtonProps={{ danger: true }}
        confirmLoading={deleting}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ open: false, id: "", name: "" })}
        className="standardHeader standardFooter"
      >
        <p>
          {t("Delete role")} <strong>"{deleteModal.name}"</strong>?{" "}
          {t(
            "Folders using this role will need to be updated."
          )}
        </p>
      </Modal>
    </div>
  );
};

export default DocumentsSettings;
